/**
 * Estado comercial da conta — fonte: user.trial (retornado por /auth/me,
 * calculado por GetTrialStatusService no backend)
 *
 * @typedef {Object} TrialInfo
 * @property {boolean}     inTrial       - true enquanto trial está vigente (inclui o último dia)
 * @property {boolean}     expired       - trial venceu SEM contratação de plano
 * @property {number|null} daysRemaining - dias restantes além de hoje (0 = hoje é o último dia)
 * @property {string|null} expiresAt     - último dia com acesso (YYYY-MM-DD) ou null
 * @property {string|null} startedAt     - início do trial (YYYY-MM-DD) ou null
 */

/**
 * Motivo de bloqueio retornado pelo backend (campo `code` no body do 403).
 *
 * @typedef {'TRIAL_EXPIRED' | 'INVOICE_OVERDUE' | 'SUBSCRIPTION_CANCELLED' | 'COMPANY_INACTIVE'} AccountBlockReason
 */

/**
 * Mensagens de exibição para cada motivo de bloqueio.
 *
 * @type {Record<AccountBlockReason, string>}
 */
export const BLOCK_REASON_MESSAGES = {
  TRIAL_EXPIRED: 'Seu período de teste encerrou. Escolha um plano para continuar usando o sistema.',
  INVOICE_OVERDUE:
    'Identificamos fatura vencida e pendente de pagamento. Regularize para recuperar o acesso.',
  SUBSCRIPTION_CANCELLED:
    'Sua assinatura foi cancelada. Reative seu plano para recuperar o acesso.',
  COMPANY_INACTIVE: 'Sua empresa está inativa. Entre em contato com o suporte para regularizar.',
};

/**
 * Títulos para cada motivo de bloqueio.
 *
 * @type {Record<AccountBlockReason, string>}
 */
export const BLOCK_REASON_TITLES = {
  TRIAL_EXPIRED: 'Período de teste encerrado',
  INVOICE_OVERDUE: 'Acesso bloqueado por fatura',
  SUBSCRIPTION_CANCELLED: 'Assinatura cancelada',
  COMPANY_INACTIVE: 'Empresa inativa',
};

/**
 * Detecta trial vencido sem contratação de plano.
 *
 * Confia exclusivamente em trial.expired: o backend já diferencia expiração
 * natural (expired=true) de conversão por contratação de plano (expired=false).
 * Não inferir por daysRemaining — ele é 0 tanto no último dia do trial quanto
 * após conversão.
 *
 * @param {TrialInfo} trial
 */
export const isTrialExpired = (trial) => trial?.expired === true;

/**
 * Resolve motivo de bloqueio com fallback para fatura pendente.
 *
 * @param {string | boolean | null | undefined} companyStatus
 * @param {TrialInfo} trial
 * @returns {AccountBlockReason}
 */
export const resolveAccountBlockReason = (companyStatus, trial) => {
  const status = typeof companyStatus === 'string' ? companyStatus.toLowerCase() : companyStatus;

  if (status === 'inactive') return 'COMPANY_INACTIVE';
  if (status === 'cancelled') return 'SUBSCRIPTION_CANCELLED';
  if (status === 'blocked' || status === 'overdue' || status === 'past_due')
    return 'INVOICE_OVERDUE';
  if (isTrialExpired(trial)) return 'TRIAL_EXPIRED';

  // Em dúvida, prioriza faturamento em vez de trial para evitar mensagem incorreta.
  return 'INVOICE_OVERDUE';
};

/**
 * Retorna título e mensagem do bloqueio.
 */
export const getAccountBlockInfo = (companyStatus, trial) => {
  const reason = resolveAccountBlockReason(companyStatus, trial);
  return {
    reason,
    title: BLOCK_REASON_TITLES[reason],
    message: BLOCK_REASON_MESSAGES[reason],
  };
};

/**
 * Threshold em dias para considerar trial "expirando em breve".
 */
export const TRIAL_EXPIRING_SOON_DAYS = 3;
